import React from 'react'
import { InferType } from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { EditResidentUnitsForm } from '../EditResidentUnitsForm'
import {
  societyUserUnitsUpdateSchema,
  societyUserUnitUpdateSchema,
} from '../../../../forms/schemas/society_user_unit_update'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { IUserModel } from '../../../../interfaces/models/users.interfaces'
import { getUserFullName } from '../../../../helpers/user'
import { updateUserUnits } from '../../../../api/societies'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { unitQueries } from '../../../../api/units'

interface EditResidentUnitsModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  user: IUserModel
}

export const EditResidentUnitsModal = ({
  children,
  show,
  close,
  user,
}: EditResidentUnitsModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()
  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('flashMessage.changesSaved')
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('flashMessage.changesNotSavedError')
    )
  }
  const client = useQueryClient()
  const { mutate: mutationUpdateUserUnits, isPending: IsUpdatingUserUnits } =
    useMutation({
      mutationFn: async (params: {
        formEntrances: InferType<typeof societyUserUnitUpdateSchema>[]
        existingEntrances: InferType<typeof societyUserUnitUpdateSchema>[]
      }) => {
        await updateUserUnits(params.formEntrances, params.existingEntrances)
      },
      onSuccess: async () => {
        await client.invalidateQueries({
          queryKey: unitQueries.getUnitsPerSociety(society?._id).queryKey,
        })

        showSuccessMessage()
        close()
      },
      onError: (error) => {
        showErrorMessage()
        captureException(error)
      },
    })

  const onSubmit = (
    data: InferType<typeof societyUserUnitsUpdateSchema>,
    existingUserUnits: InferType<typeof societyUserUnitUpdateSchema>[]
  ): void => {
    mutationUpdateUserUnits({
      formEntrances: data.units,
      existingEntrances: existingUserUnits,
    })
  }

  const onError = (): void => {
    showErrorMessage()
  }

  const loading = IsUpdatingUserUnits || isLoadingSociety

  const fullName = getUserFullName(user)
  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: <ModalHeader onClose={close}>{fullName}</ModalHeader>,
          body: (
            <ModalBody>
              {society && (
                <EditResidentUnitsForm
                  onError={onError}
                  onSubmit={onSubmit}
                  loading={loading}
                  society={society}
                  user={user}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
